import { FC, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useDispatch } from 'react-redux';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { Header } from 'dibs-elements/exports/Header';
import { Button } from 'dibs-elements/exports/Button';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';
import { FormattedMessage } from 'dibs-react-intl';
import { setExitCheckoutAction } from '../../actions/checkoutUIActions';
import { useCheckoutSelector } from '../../reducers/root';
import { AbandonCheckoutModal_item$key } from './__generated__/AbandonCheckoutModal_item.graphql';

import styles from './styles/AbandonCheckoutModal.scss';

type Props = {
    item: AbandonCheckoutModal_item$key;
    isOpen: boolean;
    onClose: () => void;
};

const itemFragment = graphql`
    fragment AbandonCheckoutModal_item on Item {
        localizedPdpUrl
    }
`;

const AbandonCheckoutModal: FC<Props> = ({ isOpen, onClose, item: itemRef }) => {
    const dispatch = useDispatch();
    const allowExitCheckout = useCheckoutSelector(state => state.ui.allowExitCheckout);
    const item = useFragment(itemFragment, itemRef);

    useEffect(() => {
        if (isOpen) {
            dispatch(setExitCheckoutAction(true));
        }
        return () => {
            dispatch(setExitCheckoutAction(false));
        };
    }, [isOpen, dispatch]);

    const returnToCheckout = (): void => {
        trackEvent(
            {
                category: 'abandoned checkout',
                action: 'abandon page modal',
                label: 'keep checking out click',
                isInteractiveEvent: true,
                eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
                interaction_type: interactionTypeConstants.INTERACTION_TYPE_CHECKOUT_ABANDON_PAGE,
                step_interaction_name:
                    stepInteractionConstants.STEP_INTERACTION_ABANDON_CHECKOUT_KEEP_CHECKING_OUT,
            },
            null
        );
        onClose();
    };

    const goToPdp = (): void => {
        if (allowExitCheckout) {
            trackEvent(
                {
                    category: 'abandoned checkout',
                    action: 'abandon page modal',
                    label: 'return to item click',
                    isInteractiveEvent: true,
                    eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
                    interaction_type:
                        interactionTypeConstants.INTERACTION_TYPE_CHECKOUT_ABANDON_PAGE,
                    step_interaction_name:
                        stepInteractionConstants.STEP_INTERACTION_ABANDON_CHECKOUT_RETURN_TO_ITEM,
                },
                null
            );
            if (item?.localizedPdpUrl) {
                window.location.assign(
                    handleLocaleUrl(item.localizedPdpUrl, GLOBAL_CLIENT_ONLY_LOCALE)
                );
            } else {
                onClose();
            }
        }
    };

    return (
        <ModalContainer
            isOpen={isOpen}
            onClose={returnToCheckout}
            modalPosition="center"
            mobileModalSize="contentHeight"
            modalClass={styles.modalContainer}
        >
            <ModalCloseButton onClick={returnToCheckout} />
            <ModalBody>
                <Header
                    title={
                        <FormattedMessage
                            id="checkout.shared.abandonCheckoutModal.header"
                            defaultMessage="You haven't finished checking out. If you leave, your changes may not be saved."
                        />
                    }
                    htmlElementType="h2"
                />
                <div className={styles.buttonContainer}>
                    <Button onClick={returnToCheckout} dataTn="close-abandon-checkout-modal">
                        <FormattedMessage
                            id="checkout.shared.abandonCheckoutModal.close"
                            defaultMessage="Keep Checking Out"
                        />
                    </Button>
                    <Button
                        type="secondary"
                        onClick={goToPdp}
                        dataTn="exit-from-checkout-through-abandon-checkout-modal"
                    >
                        <FormattedMessage
                            id="checkout.shared.abandonCheckoutModal.abandon"
                            defaultMessage="Return to item"
                        />
                    </Button>
                </div>
            </ModalBody>
        </ModalContainer>
    );
};

export default AbandonCheckoutModal;
